import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da2ad33k8r5te5mqskm0";

type Status = "idle" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("提交成功，感谢你的关注！");
      return;
    }

    const email = String(fd.get("email") || "").trim();
    if (!email) {
      setStatus("error");
      setMessage("请填写你的邮箱地址。");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "website_alt" && value) payload.append(key, String(value));
    });

    setSubmitting(true);
    setStatus("idle");
    setMessage("");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || (parsed?.message as string | undefined) || responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        setMessage(serverMsg || "提交成功，感谢你的订阅！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交未成功，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <section id="contact" className="bg-background-100/60 py-14 md:py-20">
      <div className="section-x grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700">
            <span className="h-2 w-2 rounded-full bg-primary-500"></span>
            联系与订阅
          </p>
          <h2 className="mt-2 font-heading text-3xl text-foreground-900 md:text-4xl">不错过任何一部好漫画</h2>
          <p className="mt-4 leading-relaxed text-foreground-600">
            订阅我们的追更资讯，第一时间获取热门作品推荐、平台更新与阅读攻略；也欢迎留下你的建议与想看的选题，我们会认真阅读每一条留言。
          </p>

          <div className="mt-8 space-y-4">
            <div className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className="ri-mail-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">商务与合作</p>
                <p className="text-sm text-foreground-600">hello@manyueguide.com</p>
              </div>
            </div>
            <div className="flex items-center gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                <i className="ri-wechat-line text-xl"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">读者交流</p>
                <p className="text-sm text-foreground-600">可在评论区或留言表单中与我们互动</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8">
          <h3 className="text-lg font-semibold text-foreground-900">订阅追更资讯</h3>
          <p className="mt-1 text-sm text-foreground-600">留下你的信息，我们会定期发送精选推荐。</p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4" data-readdy-form="comic-newsletter">
            <div className="form-honeypot" aria-hidden="true">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
                aria-hidden="true"
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="nickname" className="text-sm text-foreground-700">
                  你的昵称
                </label>
                <input id="nickname" name="nickname" type="text" placeholder="例如：阿澈" className={inputClass} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm text-foreground-700">
                  邮箱地址 <span className="text-primary-600">*</span>
                </label>
                <input id="email" name="email" type="email" required placeholder="you@example.com" className={inputClass} />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="type" className="text-sm text-foreground-700">
                你常看哪种漫画
              </label>
              <select id="type" name="comic_type" className={inputClass} defaultValue="">
                <option value="" disabled>
                  请选择漫画类型
                </option>
                <option value="国漫">国漫</option>
                <option value="日漫">日漫</option>
                <option value="韩漫">韩漫</option>
                <option value="美漫">美漫</option>
                <option value="都看">都看</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm text-foreground-700">
                想看的选题或建议
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="写下你想看的攻略选题，或对我们的建议……"
                className={`${inputClass} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "提交中…" : "立即订阅"}
              {!submitting && <i className="ri-send-plane-line"></i>}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-800">
                <i className="ri-checkbox-circle-line"></i>
                {message}
              </p>
            )}
            {status === "error" && (
              <p className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                <i className="ri-error-warning-line"></i>
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}